"use client";

import type { Usp } from "@/lib/content/types";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";
import type { LiveWin } from "@/lib/data";
import type { BankrollTile, BigWin } from "@/lib/api/mappers";
import { formatAmount, shortenAddress } from "@/lib/format/amount";
import { cn } from "@/lib/cn";
import { EASE, fadeUp, inView, stagger } from "@/lib/motion";
import { explorerAddressUrl } from "@/lib/explorers";
import { Icon } from "@/components/ui/Icon";
import { useModalChrome } from "@/components/ui/Modal";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { Avatar, Panel, PanelHeader } from "@/components/ui/primitives";
import { useContent } from "@/components/shell/ContentContext";
import { RailArrows, RailTrack, useRail } from "./Rail";

const USP_ICON = { cashout: "bolt", chat: "help", eye: "eye" } as const;

export function UspRow({ items }: { items: Usp[] }) {
  const usps = items;
  return (
    <motion.div
      role="region"
      aria-label="Why play here"
      tabIndex={0}
      variants={stagger(0, 0.08)}
      initial="hidden"
      whileInView="show"
      viewport={inView}
      className="scrollbar-none flex items-center gap-[16px] overflow-x-auto overflow-y-hidden"
    >
      {usps.map((u) => (
        <motion.div
          key={u.title}
          variants={fadeUp}
          className="group flex w-[280px] shrink-0 flex-col items-start justify-center gap-[12px] rounded-2xl bg-elevation-1 p-[12px] transition-colors duration-300 hover:bg-elevation-2 md:w-auto md:min-w-[280px] md:flex-1 md:shrink"
        >
          <div className="flex w-full items-center gap-[12px]">
            <span className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-rounded border border-border bg-elevation-2 text-brand-bold transition-colors duration-300 group-hover:bg-brand group-hover:text-fg-on-primary">
              <Icon name={USP_ICON[u.icon as keyof typeof USP_ICON]} size={18} />
            </span>
            <div className="flex min-w-0 flex-1 flex-col items-start gap-[2px]">
              <p className="w-full truncate text-[14px] font-bold leading-[1.6] text-fg">{u.title}</p>
              <p className="w-full text-[12px] font-normal leading-[1.6] text-fg-subtle">{u.body}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

/* The USP row's Strip style (Capella's, after Betmode's ticker): the points
   as figures in a ruled band — each title large in the heading face, its
   text a small label beside it, the first with a live dot. Wraps to two or
   three a row on narrower screens. */
export function StatStrip({ items }: { items: Usp[] }) {
  return (
    <motion.div
      role="region"
      aria-label="At a glance"
      variants={stagger(0, 0.06)}
      initial="hidden"
      whileInView="show"
      viewport={inView}
      /* As many columns as there are items, up to 5, so 3 items share the
         width rather than crowding 3 of 5 columns. The cell may shrink and its
         title wrap: in a wide display face (Unbounded, StarBet) a title is
         wider than its column and ran into the next one. */
      className={cn(
        "grid grid-cols-2 border-y border-border",
        items.length >= 5 ? "sm:grid-cols-3 lg:grid-cols-5" : items.length === 4 ? "sm:grid-cols-4" : items.length === 3 ? "sm:grid-cols-3" : "",
      )}
    >
      {items.map((u, i) => (
        <motion.div
          key={`${u.title}-${i}`}
          variants={fadeUp}
          className="flex min-h-[75px] min-w-0 items-center justify-center gap-x-[10px] border-r border-border px-[12px] py-[12px] text-left last:border-r-0 lg:min-h-[92px]"
        >
          {i === 0 && <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-highlight shadow-[0_0_16px_var(--color-highlight)]" />}
          <span className="min-w-0 font-heading text-[18px] font-extrabold leading-[1.1] text-fg [overflow-wrap:anywhere] lg:text-[22px]">{u.title}</span>
          {u.body && <span className="min-w-0 max-w-[140px] shrink text-[9px] font-black uppercase tracking-[0.09em] text-fg-subtle">{u.body}</span>}
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ------------------------------------------------------- live wins strip */

export function LiveWinsStrip({ wins }: { wins: LiveWin[] }) {
  const doubled = [
    ...wins.map((w) => ({ win: w, clone: false })),
    ...wins.map((w) => ({ win: w, clone: true })),
  ];
  const [paused, setPaused] = useState(false);
  return (
    <div
      className={cn(
        "pause-on-hover relative overflow-hidden rounded-2xl bg-elevation-1 p-[16px]",
        paused && "marquee-paused",
      )}
    >
      <MarqueePause
        paused={paused}
        onToggle={() => setPaused((value) => !value)}
        className="right-[16px] top-[16px]"
      />
      <div className="mask-fade-x">
        <div className="flex w-max animate-marquee gap-[8px]">
          {doubled.map((d, i) => (
            <LiveWinCard key={i} win={d.win} clone={d.clone} />
          ))}
        </div>
      </div>
      <span className="absolute left-[16px] top-[16px] z-20 flex h-[24px] items-center gap-[6px] rounded-br-[8px] bg-elevation-1 px-[5px] text-[12px] font-bold leading-[1.6] text-fg">
        <span className="glow-highlight h-[6px] w-[6px] rounded-full bg-highlight" />
        LIVE WINS
      </span>
    </div>
  );
}

function MarqueePause({
  paused,
  onToggle,
  className,
}: {
  paused: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={paused}
      aria-label={paused ? "Play live wins" : "Pause live wins"}
      className={cn(
        "absolute z-20 grid h-[24px] w-[24px] place-items-center rounded-md bg-elevation-2 text-fg transition-colors duration-300 hover:bg-elevation-3",
        className,
      )}
    >
      <Icon name={paused ? "play" : "pause"} size={12} />
    </button>
  );
}

function LiveWinCard({ win, clone }: { win: LiveWin; clone?: boolean }) {
  return (
    <Link
      href={`/game/${win.game.slug ?? win.game.id}`}
      aria-hidden={clone || undefined}
      tabIndex={clone ? -1 : undefined}
      className="flex w-[75px] shrink-0 flex-col items-start gap-[6px]"
    >
      <span
        className={cn(
          "relative grid aspect-[330/440] w-full place-items-center overflow-hidden rounded-lg px-1 text-center text-[9px] font-black uppercase leading-[1.05] text-white",
          win.big && "glow-brand border border-brand",
        )}
        style={{ background: `linear-gradient(155deg, ${win.game.from}, ${win.game.to})` }}
      >
        {win.game.thumbnailUrl ? (
          <RemoteImage src={win.game.thumbnailUrl} alt={win.game.title} width={75} height={100} />
        ) : (
          win.game.title
        )}
      </span>
      <span className="flex w-full flex-col items-start gap-[2px]">
        <span className="flex w-full items-center justify-center gap-[2px]">
          <span className="text-glow-highlight text-[12px] font-bold leading-[1.6]">{win.amount}</span>
        </span>
        <span className="flex w-full flex-col items-center justify-center">
          <span
            className={cn(
              "flex items-center justify-center rounded-lg border px-[4px] py-[2px] text-[9px] font-bold leading-[1.5] text-fg",
              win.big ? "border-brand bg-brand" : "border-border-input bg-elevation-3",
            )}
          >
            {win.multiplier}
          </span>
        </span>
        <span className="flex h-[13px] w-full items-center justify-center gap-[2px]">
          <Avatar name={win.player} size={11} />
          <span
            className={cn(
              "truncate text-[9px] font-bold leading-[1.5]",
              win.player === "Hidden" ? "text-fg-subtle" : "text-fg",
            )}
          >
            {win.player}
          </span>
        </span>
      </span>
    </Link>
  );
}

/* --------------------------------------------------- compact ticker line */

export function LiveWinsTicker({ wins }: { wins: LiveWin[] }) {
  const doubled = [...wins, ...wins];
  const [paused, setPaused] = useState(false);
  return (
    <div
      className={cn(
        "pause-on-hover relative overflow-hidden rounded-2xl bg-elevation-1 p-[8px]",
        paused && "marquee-paused",
      )}
    >
      <div className="mask-fade-x">
        <div className="flex w-max animate-marquee-slow gap-[4px]">
          {doubled.map((w, i) => (
            <div
              key={i}
              className={cn(
                "flex shrink-0 items-center gap-[4px] rounded-lg border bg-elevation-2 py-[4px] pl-[4px] pr-[8px]",
                w.big ? "glow-brand-soft border-brand-bold" : "border-border-input",
              )}
            >
              <span
                className="relative h-[30px] w-[30px] shrink-0 overflow-hidden rounded-md"
                style={{ background: `linear-gradient(155deg, ${w.game.from}, ${w.game.to})` }}
              >
                {w.game.thumbnailUrl && (
                  <RemoteImage src={w.game.thumbnailUrl} width={30} height={30} />
                )}
              </span>
              <span className="flex items-center gap-[6px]">
                <span className="flex items-center gap-[2px]">
                  <Avatar name={w.player} size={14} />
                  <span
                    className={cn(
                      "text-[9px] font-bold leading-[1.5]",
                      w.player === "Hidden" ? "text-fg-subtle" : "text-fg",
                    )}
                  >
                    {w.player}
                  </span>
                </span>
                <span className="text-[9px] font-bold leading-[1.5] text-fg-subtle">{w.game.title}</span>
                <span className="text-glow-highlight text-[9px] font-bold leading-[1.5]">{w.amount}</span>
                <span
                  className={cn(
                    "flex items-center rounded-lg border px-[4px] py-[2px] text-[9px] font-bold leading-[1.5] text-fg",
                    w.big ? "border-brand bg-brand" : "border-border-input bg-elevation-3",
                  )}
                >
                  {w.multiplier}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>
      <MarqueePause
        paused={paused}
        onToggle={() => setPaused((value) => !value)}
        className="right-[8px] top-1/2 -translate-y-1/2"
      />
    </div>
  );
}

/* -------------------------------------------------------------- big wins */

export function BigWins({ wins }: { wins: BigWin[] }) {
  const rail = useRail();
  return (
    <Panel className="overflow-hidden">
      <PanelHeader
        icon="rocket"
        title="Big Wins"
        action={
          <>
            <Link
              href="/c/recent"
              className="hidden h-[38px] items-center rounded-lg bg-elevation-2 px-[12px] text-[12px] font-bold leading-[1.6] text-fg transition-colors hover:bg-elevation-3 sm:inline-flex"
            >
              View All
            </Link>
            <RailArrows state={rail.state} scrollBy={rail.scrollBy} />
          </>
        }
      />
      <RailTrack railRef={rail.ref} label="Big Wins" onScroll={rail.measure} className="p-[16px]">
        {wins.map((w, i) => (
          <article key={i} className="flex h-[198px] w-[343px] shrink-0 snap-start items-center">
            <span
              className="relative h-full w-[149px] shrink-0 overflow-hidden rounded-l-lg"
              style={{ background: `linear-gradient(155deg, ${w.game.from}, ${w.game.to})` }}
            >
              {w.game.thumbnailUrl && (
                <RemoteImage src={w.game.thumbnailUrl} width={149} height={198} />
              )}
              {w.game.rtp && (
                <span className="absolute left-0 top-0 flex h-[26px] items-start gap-[2px] p-[6px]">
                  <span className="flex h-[16px] items-center justify-center rounded-sm bg-elevation-3 px-[3px] text-[9px] font-bold leading-[1.5] text-fg">
                    RTP {w.game.rtp}
                  </span>
                </span>
              )}
              {w.game.tag && (
                <span className="absolute right-0 top-0 flex flex-col items-center justify-center p-[6px]">
                  <span className="flex h-[16px] items-center justify-center rounded-sm bg-error px-[3px] text-[9px] font-bold leading-[1.5] text-fg">
                    {w.game.tag}
                  </span>
                </span>
              )}
              {!w.game.thumbnailUrl && (
                <span className="absolute inset-x-0 bottom-[10px] px-2 text-center text-[12px] font-black uppercase leading-[0.95] text-white">
                  {w.game.title}
                </span>
              )}
            </span>
            <div className="flex h-full min-w-0 flex-1 flex-col justify-center gap-[12px] rounded-r-lg bg-elevation-2 p-[12px]">
              <div className="flex w-full flex-col items-start gap-[2px]">
                <h3 className="h-[22px] w-full truncate text-[14px] font-extrabold leading-[1.6] text-fg">
                  {w.game.title}
                </h3>
              </div>
              <dl className="flex w-full flex-col items-start gap-[2px]">
                <Row label="Payout" value={<span className="text-glow-highlight font-bold">{w.payout}</span>} />
                <Row label="Multiplier" value={<span className="font-bold text-fg">{w.multiplier}</span>} />
                <Row label="Bet" value={<span className="font-bold text-fg">{w.bet}</span>} />
                <Row
                  label="Player"
                  value={
                    <span className="flex items-center gap-[2px] font-bold text-fg">
                      <Avatar name={w.player} size={16} />
                      {w.player}
                    </span>
                  }
                />
              </dl>
              <span className="flex h-[30px] w-full items-center justify-center gap-[8px] rounded-md bg-elevation-3 p-[8px] text-[12px] font-bold leading-[1.6] text-fg-subtle">
                <Icon name="link" size={12} className="shrink-0" />
                <span className="truncate">{w.hash}</span>
              </span>
            </div>
          </article>
        ))}
      </RailTrack>
    </Panel>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex w-full items-center justify-between text-[12px] leading-[1.6]">
      <dt className="font-normal text-fg-subtle">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

/* -------------------------------------------------------------- bankroll */

type BankrollCard = BankrollTile & { coldAddress?: string | null; coldBalance?: string | null };

/* Both figures are base-unit strings from chain; the tile shows their sum.
   A missing read contributes nothing rather than blocking the other. */
const summedBalance = (card: BankrollCard): string | null => {
  const parts = [card.balance, card.coldBalance].filter((value): value is string => value !== null && value !== undefined);
  if (parts.length === 0) return null;
  return parts.reduce((total, value) => total + BigInt(value), 0n).toString();
};

export function Bankroll({ tiles }: { tiles?: BankrollCard[] }) {
  const cards = tiles ?? [];
  const rail = useRail();
  const [info, setInfo] = useState<BankrollCard | null>(null);

  if (cards.length === 0) return null;

  return (
    <Panel className="overflow-hidden">
      <PanelHeader icon="bank" title="Bankroll" action={<RailArrows state={rail.state} scrollBy={rail.scrollBy} />} />
      <RailTrack railRef={rail.ref} label="Bankroll" onScroll={rail.measure} className="gap-[16px]! p-[16px]">
        {cards.map((b) => (
          <div
            key={b.id}
            className="surface-sheen-2 flex shrink-0 grow basis-[378.67px] snap-start flex-col items-start gap-[16px] rounded-xl border border-border p-[24px]"
          >
            <div className="flex w-full items-center justify-between">
              <p className="text-[12px] font-bold uppercase leading-[1.6] tracking-[0.6px] text-fg-subtle">{b.label}</p>
              <button
                type="button"
                onClick={() => setInfo(b)}
                aria-label={`${b.label} details`}
                aria-haspopup="dialog"
                className="grid h-[20px] w-[20px] place-items-center text-fg-subtle transition-colors hover:text-fg"
              >
                <Icon name="info" size={20} />
              </button>
            </div>
            <div className="h-[39px] w-full overflow-hidden">
              {/* The pool's and the cold wallet's real on-chain liquidity,
                  summed. Until a read lands the symbol stands in — never an
                  invented number. Here the ticker is the token's own
                  (USDSM), not the dollar label the rest of the site uses. */}
              <p className="text-h3 font-black text-fg">
                {summedBalance(b) !== null
                  ? `${formatAmount(summedBalance(b) ?? "0", { decimals: b.decimals, significantDecimals: b.significantDecimals }, { grouped: true })} ${b.symbol}`
                  : b.symbol}
              </p>
            </div>
            <div className="flex w-full items-center gap-[8px]">
              <span className="relative h-[20px] w-[20px] shrink-0 overflow-hidden">
                <RemoteImage src={b.tokenLogoUrl} width={20} height={20} />
              </span>
              <span className="text-[12px] font-bold leading-[1.6] text-fg-subtle">{b.symbol}</span>
              <span className="flex min-w-0 flex-1 items-center justify-end gap-[4px] text-[12px] font-normal leading-[1.6] text-fg-subtle">
                <Icon name="link" size={12} className="shrink-0" />
                {shortenAddress(b.address)}
              </span>
            </div>
          </div>
        ))}
      </RailTrack>

      <BankrollInfoModal tile={info} onClose={() => setInfo(null)} />
    </Panel>
  );
}

function BankrollInfoModal({ tile, onClose }: { tile: BankrollCard | null; onClose: () => void }) {
  const panel = useModalChrome(Boolean(tile), onClose);

  const addressUrl = tile ? explorerAddressUrl(tile.chainId, tile.address) : null;
  const coldAddressUrl = tile?.coldAddress ? explorerAddressUrl(tile.chainId, tile.coldAddress) : null;

  return (
    <AnimatePresence>
      {tile && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-[2px]"
          />
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label={`${tile.label} details`}
            initial={{ opacity: 0, scale: 0.95, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.32, ease: EASE }}
            className="modal-shadow relative flex w-full max-w-[400px] flex-col gap-[16px] rounded-2xl border border-border-input bg-elevation-1 p-[24px]"
          >
            <div className="flex items-center gap-[10px]">
              <span className="relative h-[32px] w-[32px] shrink-0 overflow-hidden rounded-full">
                <RemoteImage src={tile.tokenLogoUrl} width={32} height={32} />
              </span>
              <h2 className="flex-1 text-[15.75px] font-bold leading-[1.4] text-fg">{tile.label}</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid h-[24px] w-[24px] place-items-center text-fg transition-colors hover:text-fg-muted"
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            <div className="rounded-lg bg-elevation-2 p-[16px]">
              <div className="flex items-center justify-between gap-[8px]">
                <p className="text-[12px] font-bold uppercase leading-[1.6] tracking-[0.6px] text-fg-subtle">
                  Bankroll balance
                </p>
                {addressUrl && (
                  <a
                    href={addressUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label="Bankroll wallet on the explorer"
                    className="grid h-[20px] w-[20px] shrink-0 place-items-center text-fg-subtle transition-colors hover:text-fg"
                  >
                    <Icon name="external" size={16} />
                  </a>
                )}
              </div>
              <p className="mt-[4px] text-h4 font-black text-fg">
                {tile.balance !== null
                  ? `${formatAmount(tile.balance, { decimals: tile.decimals, significantDecimals: tile.significantDecimals }, { grouped: true })} ${tile.symbol}`
                  : "Reading from chain…"}
              </p>
            </div>

            {tile.coldAddress && (
              <div className="rounded-lg bg-elevation-2 p-[16px]">
                <div className="flex items-center justify-between gap-[8px]">
                  <p className="text-[12px] font-bold uppercase leading-[1.6] tracking-[0.6px] text-fg-subtle">
                    Cold wallet balance
                  </p>
                  {coldAddressUrl && (
                    <a
                      href={coldAddressUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label="Cold wallet on the explorer"
                      className="grid h-[20px] w-[20px] shrink-0 place-items-center text-fg-subtle transition-colors hover:text-fg"
                    >
                      <Icon name="external" size={16} />
                    </a>
                  )}
                </div>
                <p className="mt-[4px] text-h4 font-black text-fg">
                  {tile.coldBalance !== null && tile.coldBalance !== undefined
                    ? `${formatAmount(tile.coldBalance, { decimals: tile.decimals, significantDecimals: tile.significantDecimals }, { grouped: true })} ${tile.symbol}`
                    : "Reading from chain…"}
                </p>
                <p className="mt-[4px] truncate text-[11px] leading-[1.5] text-fg-subtle">{tile.coldAddress}</p>
              </div>
            )}

            <p className="text-[11px] leading-[1.5] text-fg-subtle">
              Settled in {tile.symbol} on chain · every payout is written to chain and readable at these addresses.
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/* -------------------------------------------------- provider logo strip */

/* The kit's twelfth section: four flat cards, each holding a studio wordmark
   in a 64x20 slot. Real logos need assets we do not have, so the name stands
   in at the same size. */
export function StudioStrip({ names }: { names?: string[] }) {
  const studios = names ?? [];

  if (studios.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Game studios"
      tabIndex={0}
      className="scrollbar-none flex items-center gap-[16px] overflow-x-auto overflow-y-hidden"
    >
      {studios.slice(0, 4).map((s) => (
        <div
          key={s}
          className="flex h-[67px] w-[280px] shrink-0 items-center justify-center gap-[12px] rounded-xl bg-elevation-1 p-[12px] transition-colors duration-300 hover:bg-elevation-2 sm:w-auto sm:min-w-[280px] sm:flex-1 sm:shrink"
        >
          <span className="flex h-[20px] w-[64px] items-center justify-center text-center text-[8px] font-black uppercase leading-[1.1] tracking-[0.06em] text-fg-muted">
            {s}
          </span>
        </div>
      ))}
    </div>
  );
}
